import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import ProtectedRoute, {
  CandidateRoute,
  EmployerRoute,
  AgencyRoute,
  AdminRoute
} from './components/common/ProtectedRoute';

// Public Pages
import Login from './pages/Login';
import AccountUnlock from './pages/AccountUnlock';
import SetNewPassword from './pages/SetNewPassword';
import Register from './pages/Register';
import Verification from './pages/Verification';
import MFAVerification from './pages/MFAVerification';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import JobFeed from "./pages/CandidateJobFeed/JobFeed";
import MyApplications from "./pages/JobApplication/CandidateApplication/MyApplications"
import SubmittedApplications from "./pages/JobApplication/EmployerApplication/SubmittedApplications"
import JobPostingManagement from './pages/JobPostingManagement';

// References
import CandidateReferences from './pages/CandidateReferences';
import EmployerReferences from "./pages/EmployerReferences";


// Protected Pages - Shared
import ChangePassword from './pages/ChangePassword';
import Profile from './pages/Profile';
import Sections from './pages/Sections';
import EmployerScreening from './pages/EmployerScreening';

// Dashboard Pages (Role-Specific)
import Dashboard from './pages/Dashboard'; // ADMIN Dashboard 
import CandidateDashboard from './pages/CandidateDashboard';
import EmployerDashboard from './pages/EmployerDashboard';
import AgencyDashboard from './pages/AgencyDashboard';

// Background Check Component
import BackgroundCheckDashboard from './components/backgroundcheck/BackgroundCheckDashboard';

// Styles
import './App.css';

/**
 * RoleBasedDashboardRedirect
 * Automatically redirects users to their role-specific dashboard
 */
const RoleBasedDashboardRedirect = () => {
  const { userRole, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  console.log('🎯 RoleBasedDashboardRedirect: Redirecting user with role:', userRole);

  // Redirect based on user role
  switch (userRole) {
    case 'EMPLOYER':
      console.log('→ Redirecting to /employer/dashboard');
      return <Navigate to="/employer/dashboard" replace />;

    case 'AGENCY':
      console.log('→ Redirecting to /agency/dashboard');
      return <Navigate to="/agency/dashboard" replace />;

    case 'ADMIN':
      console.log('→ Redirecting to /admin/dashboard');
      return <Navigate to="/admin/dashboard" replace />;

    case 'CANDIDATE':
    default:
      console.log('→ Redirecting to /candidate/dashboard');
      return <Navigate to="/candidate/dashboard" replace />;
  }
};

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <BrowserRouter>
          <Routes>
            {/* ==================== PUBLIC ROUTES ==================== */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verification" element={<Verification />} />
            <Route path="/mfa-verification" element={<MFAVerification />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/unlock" element={<AccountUnlock />} />
            <Route path="/set-new-password" element={<SetNewPassword />} />
            <Route path="/jobs" element={<JobFeed />} />
            <Route path="/my-applications" element={<MyApplications />} />
            <Route path="/jobs/:jobId/applications" element={<SubmittedApplications />} />

            {/* ==================== GENERIC DASHBOARD ROUTE ==================== */}
            {/* This redirects to role-specific dashboard */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <RoleBasedDashboardRedirect />
                </ProtectedRoute>
              }
            />

            {/* ==================== CANDIDATE ROUTES ==================== */}
            <Route
              path="/candidate/dashboard"
              element={
                <CandidateRoute>
                  <CandidateDashboard />
                </CandidateRoute>
              }
            />
            <Route
              path="/candidate/profile"
              element={
                <CandidateRoute>
                  <Profile />
                </CandidateRoute>
              }
            />
            <Route
              path="/candidate/sections"
              element={
                <CandidateRoute>
                  <Sections />
                </CandidateRoute>
              }
            />
            <Route
              path="/candidate/references"
              element={
                <CandidateRoute>
                  <CandidateReferences />
                </CandidateRoute>
              }
            />

            <Route
              path="/candidate/background-check"
              element={
                <CandidateRoute>
                  <BackgroundCheckDashboard userType="candidate" />
                </CandidateRoute>
              }
            />

            {/* ==================== EMPLOYER ROUTES ==================== */}
            <Route
              path="/employer/dashboard"
              element={
                <EmployerRoute>
                  <EmployerDashboard />
                </EmployerRoute>
              }
            />
            <Route
              path="/employer/profile"
              element={
                <EmployerRoute>
                  <Profile />
                </EmployerRoute>
              }
            />
            <Route 
            path="/employer/references" 
            element={
              <EmployerRoute>
                <EmployerReferences />
                </EmployerRoute>
            } />
            <Route
  path="/employer/jobs"
  element={
    <EmployerRoute>
      <JobPostingManagement />
    </EmployerRoute>
  }
/>


            <Route
              path="/employer/background-check"
              element={
                <EmployerRoute>
                  <BackgroundCheckDashboard userType="employer" />
                </EmployerRoute>
              }
            />
            <Route
              path="/employer/candidates"
              element={
                <EmployerRoute>
                  <div style={{ padding: '40px', textAlign: 'center' }}>
                    <h2>Candidate Search</h2>
                    <p>Coming soon...</p>
                  </div>
                </EmployerRoute>
              }
            />
            {/* ==================== EMPLOYER SCREENING ROUTES ==================== */}

            <Route
              path="/employer/applications/:applicationId/screening"
              element={
                <ProtectedRoute allowedRoles={['EMPLOYER']}>
                  <EmployerScreening />
                </ProtectedRoute>
              }
            />


            {/* ==================== AGENCY ROUTES ==================== */}
            <Route
              path="/agency/dashboard"
              element={
                <AgencyRoute>
                  <AgencyDashboard />
                </AgencyRoute>
              }
            />
            <Route
              path="/agency/assignments"
              element={
                <AgencyRoute>
                  <BackgroundCheckDashboard userType="agency" />
                </AgencyRoute>
              }
            />
            <Route
              path="/agency/reports"
              element={
                <AgencyRoute>
                  <div style={{ padding: '40px', textAlign: 'center' }}>
                    <h2>Verification Reports</h2>
                    <p>Coming soon...</p>
                  </div>
                </AgencyRoute>
              }
            />

            {/* ==================== ADMIN ROUTES ==================== */}
            <Route
              path="/admin/dashboard"
              element={
                <AdminRoute>
                  <Dashboard />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <AdminRoute>
                  <div style={{ padding: '40px', textAlign: 'center' }}>
                    <h2>User Management</h2>
                    <p>Coming soon...</p>
                  </div>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/verifications"
              element={
                <AdminRoute>
                  <BackgroundCheckDashboard userType="admin" />
                </AdminRoute>
              }
            />

            {/* ==================== SHARED PROTECTED ROUTES ==================== */}
            {/* All authenticated users can access these */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sections"
              element={
                <ProtectedRoute>
                  <Sections />
                </ProtectedRoute>
              }
            />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePassword />
                </ProtectedRoute>
              }
            />
            <Route
              path="/background-check"
              element={
                <ProtectedRoute>
                  <BackgroundCheckDashboard />
                </ProtectedRoute>
              }
            />

            {/* ==================== CATCH ALL ROUTE ==================== */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
